import { Form, Formik } from 'formik'
import React, { useState } from 'react'
import { Button, DialogActions, IconButton, makeStyles, Typography } from '@material-ui/core'
import { useRedirect, useTranslate } from 'react-admin'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import PropTypes from 'prop-types'
import MyTextField from '../../fields/MyTextField'
import subscriptionFormStyles from './subscriptionForm.styles'
import ButtonWithLoader from '../../../ui/ButtonWithLoader/ButtonWithLoader'
import ContainerPadding from '../../../containerPadding/containerPadding'
import COLORS from '../../../../utils/constants/colors'
import QueryKeys from '../../../../utils/constants/queryKeys'
import getSubscriptionDetails from '../../../../Apis/subscriptions/getSubscriptionDetails'
import useUrlSearchParams from '../../../../hooks/useUrlSearchParams'
import LoaderDots from '../../../loaders/LoaderDots/LoaderDots'
import orgIsInSubscriptionState from '../../../../utils/helpers/orgIsInSubscriptionState'
import SUBSCRIPTION_STATES from '../../../../utils/constants/subscriptionStates'
import cancelSubscription from '../../../../Apis/subscriptions/cancelSubscription'
import activateCoupon from '../../../../Apis/subscriptions/activateCoupon'
import getCountries from '../../../../Apis/organisation/getCountries'
import LoaderCircle from '../../../loaders/LoaderCircle/LoaderCircle'
import DeleteIcon from '@material-ui/icons/Delete'
import changeCard from '../../../../Apis/subscriptions/changeCard'
import removeCard from '../../../../Apis/subscriptions/removeCard'
import localStorageIds from '../../../../utils/constants/localStorageIds'
import { setLocalStorageValue } from '../../../../utils/helpers/localStorageHelperMethods'
import DialogWithCloseIcon from '../../../DialogWithCloseIcon/DialogWithCloseIcon'
import TextButton from '../../../ui/TextButton/TextButton'
import CancelSubscriptionDialogConfirmation from './CancelSubscriptionDialogConfirmation/CancelSubscriptionDialogConfirmation'
import getOrgSubscriptionStatus from '../../../../Apis/subscriptions/getOrgSubscriptionStatus'

const SubscriptionForm = ({ setPaymentSuccess, showCard, setTab, handleActivateSubscription, isSubActivationLoading }) => {
  const { org: orgId } = useUrlSearchParams(['org']) || {}
  const [isEditingCoupon, setIsEditingCoupon] = useState(false)
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [isRemoveCardModalOpen, setIsRemoveCardModalOpen] = useState(false)
  const [couponMessage, setCouponMessage] = useState({ message: '', isError: false })
  const externalRedirect = useRedirect()
  const translate = useTranslate()
  const queryClient = useQueryClient()
  const useStyles = makeStyles(subscriptionFormStyles)
  const classes = useStyles()

  const { data: subscriptionDetails, isLoading } = useQuery(
    [QueryKeys.GET_SUBSCRIPTION_DETAILS, orgId],
    () => getSubscriptionDetails({ orgId }),
    { enabled: Boolean(orgId) },
  )

  // Only to name the stored country — there is no country picker here any more.
  const { data: countries } = useQuery([QueryKeys.GET_COUNTRIES], () => getCountries())

  const {
    firstName, lastName, address, city, zipCode,
    countryId, country, companyName, vatNumber,
    needsInvoice, cardFourLastDigits, coupon,
  } = subscriptionDetails || {}

  const countryName = countries?.find((c) => c.id === countryId)?.name

  // --- Mutations ---

  const orgSubStatusMutation = useMutation((data) => getOrgSubscriptionStatus(data), {
    onSuccess: (res) => {
      queryClient.setQueryData([QueryKeys.GET_ORG_SUB_STATUS, orgId], res)
      setLocalStorageValue([{ id: localStorageIds.SELECTED_ORG_SUB_STATUS, value: res }])
    },
  })

  const cancelSubscriptionMutation = useMutation(() => cancelSubscription({ orgId }), {
    onSuccess: () => {
      orgSubStatusMutation.mutate({ orgId })
      queryClient.invalidateQueries(QueryKeys.GET_SUBSCRIPTION_DETAILS)
      queryClient.invalidateQueries(QueryKeys.GET_ACCOUNT_ME).then()
      setTab(0)
      setIsCancelModalOpen(false)
    },
  })

  const activateCouponMutation = useMutation(
    (data) => activateCoupon({ couponCode: data.couponCode, orgId }),
    {
      onSuccess: (res) => {
        const { id } = res?.data || {}
        if (id) {
          queryClient.invalidateQueries(QueryKeys.GET_ACCOUNT_ME).then(() => {
            setPaymentSuccess(undefined)
            setCouponMessage({
              message: `Coupon activated successfully! It is valid for ${res.data.durationInMonths} month${res.data.durationInMonths > 1 ? 's' : ''}.`,
              isError: false,
            })
          })
          queryClient.invalidateQueries(QueryKeys.GET_SUBSCRIPTION_DETAILS).then()
          setIsEditingCoupon(false)
        }
      },
      onError: (res) => {
        const message = res?.response?.data?.details || 'Coupon activation failed.'
        setPaymentSuccess(undefined)
        setCouponMessage({ message, isError: true })
      },
    },
  )

  const changeCardMutation = useMutation(() => changeCard({ orgId }), {
    onSuccess: (res) => {
      const { vivaPaymentOrderCode } = res?.data || {}
      externalRedirect(`${process.env.REACT_APP_VIVA_URL}${vivaPaymentOrderCode}`)
    },
  })

  const removeCardMutation = useMutation(() => removeCard({ orgId }), {
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKeys.GET_SUBSCRIPTION_DETAILS)
      setIsRemoveCardModalOpen(false)
    },
  })

  const hasBillingInfo = firstName && address && city && zipCode

  // --- Loading ---

  if (isLoading) {
    return (
      <ContainerPadding paddingValue='45%'>
        <LoaderDots style={{ fill: COLORS.orange, width: 70, height: 70 }} />
      </ContainerPadding>
    )
  }

  // --- Render ---

  return (
    <div className={classes.container}>
      {/* === Billing Information Section === */}
      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>Billing information</Typography>
        </div>

        {hasBillingInfo ? (
          <div className={classes.summaryText}>
            <div>{firstName} {lastName}</div>
            <div>{address}</div>
            <div>{city} {zipCode}</div>
            <div>{countryName || country}</div>
            {needsInvoice && companyName && (
              <div style={{ marginTop: 16 }}>
                <Typography style={{ fontWeight: 600, fontSize: '0.875rem', color: '#666', marginBottom: 4 }}>Invoice details</Typography>
                <div>{companyName}</div>
                <div>VAT: {vatNumber}</div>
              </div>
            )}
          </div>
        ) : (
          <Typography className={classes.summaryText}>
            No billing information added yet.
          </Typography>
        )}

        {/* Billing details are edited by Mismatch only (BUS-2976); the card below stays yours. */}
        <Typography className={classes.summaryText} style={{ marginTop: 16, color: '#666' }}>
          {translate('ra.text.billingReadOnly')}
        </Typography>
      </div>

      {/* === Payment Method Section === */}
      {showCard && (
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <Typography className={classes.sectionTitle}>Card</Typography>
            <div className={classes.sectionActions}>
              {changeCardMutation.isLoading ? (
                <LoaderCircle loading color={COLORS.blue} />
              ) : (
                <Button
                  className={classes.editButton}
                  onClick={changeCardMutation.mutate}
                  size='small'
                >
                  {cardFourLastDigits ? 'Change' : 'Add'}
                </Button>
              )}
              {cardFourLastDigits && (
                <IconButton
                  size='small'
                  onClick={() => setIsRemoveCardModalOpen(true)}
                >
                  <DeleteIcon style={{ color: COLORS.red }} />
                </IconButton>
              )}
            </div>
          </div>
          <Typography className={classes.summaryText}>
            {cardFourLastDigits
              ? `Card ending in ${cardFourLastDigits}`
              : 'No card added yet.'}
          </Typography>
        </div>
      )}

      <DialogWithCloseIcon
        open={isRemoveCardModalOpen}
        onClose={() => setIsRemoveCardModalOpen(false)}
        dialogWidth='48%'
        dialogTitle='Are you sure you want to remove your card?'
      >
        <DialogActions>
          <TextButton label={translate('ra.buttons.no')} onClick={() => setIsRemoveCardModalOpen(false)} />
          <ButtonWithLoader
            label='Remove Card'
            style={{ backgroundColor: COLORS.red }}
            onClick={() => removeCardMutation.mutate()}
            loading={removeCardMutation.isLoading}
            type='button'
          />
        </DialogActions>
      </DialogWithCloseIcon>

      {/* === Coupon Section === */}
      {showCard && (
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <Typography className={classes.sectionTitle}>Coupon</Typography>
            <div className={classes.sectionActions}>
              {!coupon && !isEditingCoupon && (
                <Button
                  className={classes.editButton}
                  onClick={() => setIsEditingCoupon(true)}
                  size='small'
                >
                  Redeem a coupon
                </Button>
              )}
            </div>
          </div>

          {isEditingCoupon && !coupon ? (
            <Formik
              initialValues={{ couponCode: '' }}
              onSubmit={(values) => activateCouponMutation.mutate(values)}
              validate={() => { if (couponMessage.message) setCouponMessage({ message: '', isError: false }) }}
            >
              {({ values }) => (
                <Form>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                    <MyTextField name='couponCode' label='Coupon code' style={{ flex: 1 }} />
                    <div className={classes.formActions} style={{ marginTop: 0 }}>
                      <ButtonWithLoader
                        variant='contained'
                        style={{ backgroundColor: COLORS.blue, color: 'white', textTransform: 'none' }}
                        disabled={values.couponCode.length <= 3}
                        label='Redeem'
                        loading={activateCouponMutation.isLoading}
                      />
                      <Button
                        variant='outlined'
                        className={classes.cancelButton}
                        onClick={() => setIsEditingCoupon(false)}
                        size='small'
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                  {couponMessage.message && (
                    <Typography
                      style={{
                        color: couponMessage.isError ? COLORS.red : COLORS.green,
                        fontSize: '0.875rem',
                        marginTop: 8,
                      }}
                    >
                      {couponMessage.message}
                    </Typography>
                  )}
                </Form>
              )}
            </Formik>
          ) : (
            <Typography className={classes.summaryText}>
              {coupon
                ? `Active coupon: ${coupon.code} (${coupon.discountPercentage}% off)`
                : 'You don\'t have an active coupon.'}
            </Typography>
          )}
        </div>
      )}


      {showCard &&
        orgIsInSubscriptionState([SUBSCRIPTION_STATES.ACTIVE, SUBSCRIPTION_STATES.GRACE]) && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => setIsCancelModalOpen(true)}
                style={{ color: COLORS.red, textTransform: 'none' }}
                size='small'
              >
                Cancel subscription
              </Button>
            </div>
            <CancelSubscriptionDialogConfirmation
              isOpen={isCancelModalOpen}
              onClose={() => setIsCancelModalOpen(false)}
              cancelSubscription={cancelSubscriptionMutation}
            />
          </>
        )}
    </div>
  )
}

SubscriptionForm.propTypes = {
  setPaymentSuccess: PropTypes.func,
  showCard: PropTypes.bool,
  setTab: PropTypes.bool,
  handleActivateSubscription: PropTypes.func,
  isSubActivationLoading: PropTypes.bool,
}

SubscriptionForm.defaultProps = {
  handleActivateSubscription: () => {},
}

export default SubscriptionForm
